import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Database,
  Plus,
  Search,
  Upload,
  Download,
  Trash2,
  Eye,
  FileText,
  FileJson,
  FileSpreadsheet,
  File,
  X,
  CheckCircle2,
  Clock,
} from 'lucide-react';

interface Dataset {
  id: string;
  name: string;
  format: string;
  size: number;
  rows?: number;
  status: string;
  createdAt: string;
}

const FORMAT_ICONS: Record<string, typeof FileText> = {
  jsonl: FileJson,
  json: FileJson,
  csv: FileSpreadsheet,
  parquet: Database,
  txt: FileText,
};

const FORMAT_COLORS: Record<string, string> = {
  jsonl: '#f0b90b',
  json: '#f0b90b',
  csv: '#0ecb81',
  parquet: '#8b5cf6',
  txt: '#848e9c',
};

export default function Datasets() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { data: datasets = [], isLoading } = useQuery<Dataset[]>({
    queryKey: ['datasets'],
    queryFn: () => api.get('/datasets').then((r) => r.data),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/datasets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Dataset uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      closeUploadModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Upload failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/datasets/${id}`),
    onSuccess: () => {
      toast.success('Dataset deleted');
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const detectFormat = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'unknown';
  };

  const filteredDatasets = datasets.filter((d) =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Datasets</h1>
          <p className="text-[#848e9c]">Manage training data for your AI models</p>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="btn-primary btn-md">
          <Upload className="h-5 w-5" />
          Upload Dataset
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#f0b90b]/10 flex items-center justify-center">
              <Database className="h-5 w-5 text-[#f0b90b]" />
            </div>
          </div>
          <div className="text-2xl font-bold text-white">{datasets.length}</div>
          <div className="text-sm text-[#848e9c]">Total Datasets</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#0ecb81]/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-[#0ecb81]" />
            </div>
          </div>
          <div className="text-2xl font-bold text-white">
            {datasets.filter((d) => d.status === 'ready').length}
          </div>
          <div className="text-sm text-[#848e9c]">Ready to Use</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#8b5cf6]/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-[#8b5cf6]" />
            </div>
          </div>
          <div className="text-2xl font-bold text-white">
            {datasets.reduce((sum, d) => sum + (d.rows || 0), 0).toLocaleString()}
          </div>
          <div className="text-sm text-[#848e9c]">Total Rows</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#1e90ff]/10 flex items-center justify-center">
              <Database className="h-5 w-5 text-[#1e90ff]" />
            </div>
          </div>
          <div className="text-2xl font-bold text-white">
            {formatSize(datasets.reduce((sum, d) => sum + d.size, 0))}
          </div>
          <div className="text-sm text-[#848e9c]">Total Size</div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between mb-6">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#848e9c]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search datasets..."
            className="input pl-11"
          />
        </div>
      </div>

      {/* Datasets table */}
      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-[#848e9c]">Loading datasets...</p>
        </div>
      ) : filteredDatasets.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
            <Database className="h-8 w-8 text-[#848e9c]" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No datasets yet</h3>
          <p className="text-[#848e9c] mb-6 max-w-sm mx-auto">
            Upload your first dataset to start training custom models
          </p>
          <button onClick={() => setShowUploadModal(true)} className="btn-primary btn-md">
            <Upload className="h-5 w-5" />
            Upload Your First Dataset
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Name</th>
                <th>Format</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Size</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDatasets.map((dataset) => {
                const FormatIcon = FORMAT_ICONS[dataset.format] || File;
                const formatColor = FORMAT_COLORS[dataset.format] || '#848e9c';
                
                return (
                  <tr key={dataset.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: formatColor + '15' }}
                        >
                          <FormatIcon className="h-5 w-5" style={{ color: formatColor }} />
                        </div>
                        <span className="font-medium text-white">{dataset.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="badge-neutral uppercase">{dataset.format}</span>
                    </td>
                    <td className="text-right font-mono text-[#848e9c]">
                      {dataset.rows?.toLocaleString() || '-'}
                    </td>
                    <td className="text-right font-mono text-[#848e9c]">
                      {formatSize(dataset.size)}
                    </td>
                    <td>
                      <div className={cn(
                        "flex items-center gap-1.5 w-fit",
                        dataset.status === 'ready' ? "text-[#0ecb81]" : "text-[#f0b90b]"
                      )}>
                        {dataset.status === 'ready' ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <Clock className="h-4 w-4" />
                        )}
                        <span className="capitalize text-sm">{dataset.status}</span>
                      </div>
                    </td>
                    <td className="text-[#848e9c] text-sm">
                      {new Date(dataset.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setSelectedDataset(dataset);
                            setShowPreviewModal(true);
                          }}
                          className="btn-ghost btn-xs"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="btn-ghost btn-xs">
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(dataset.id)}
                          className="btn-ghost btn-xs text-[#f6465d] hover:text-[#f6465d] hover:bg-[#f6465d]/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={closeUploadModal}>
          <div className="modal-content w-full max-w-lg p-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-xl font-bold text-white">Upload Dataset</h2>
              <button onClick={closeUploadModal} className="btn-ghost p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div
                className={cn(
                  "border-2 border-dashed rounded-xl p-8 text-center transition-colors",
                  dragActive
                    ? "border-[#f0b90b] bg-[#f0b90b]/5"
                    : "border-white/[0.12] hover:border-white/[0.2]"
                )}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {uploadFile ? (
                  <div>
                    <div className="w-16 h-16 rounded-2xl bg-[#0ecb81]/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-8 w-8 text-[#0ecb81]" />
                    </div>
                    <p className="text-white font-medium mb-1">{uploadFile.name}</p>
                    <p className="text-sm text-[#848e9c] mb-4">
                      {formatSize(uploadFile.size)} • {detectFormat(uploadFile.name).toUpperCase()}
                    </p>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-sm text-[#f6465d] hover:text-[#ff6b7a]"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="w-16 h-16 rounded-2xl bg-[#2b3139] flex items-center justify-center mx-auto mb-4">
                      <Upload className="h-8 w-8 text-[#848e9c]" />
                    </div>
                    <p className="text-white font-medium mb-1">
                      Drag and drop your file here
                    </p>
                    <p className="text-sm text-[#848e9c] mb-4">
                      or click to browse
                    </p>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".csv,.jsonl,.json,.parquet,.txt"
                      onChange={handleFileSelect}
                    />
                    <label
                      htmlFor="file-upload"
                      className="btn-secondary btn-sm cursor-pointer"
                    >
                      Browse Files
                    </label>
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-[#181a20] rounded-xl">
                <p className="text-xs text-[#848e9c]">
                  <strong className="text-white">Supported formats:</strong> CSV, JSONL, JSON, Parquet, TXT
                </p>
                <p className="text-xs text-[#848e9c] mt-1">
                  <strong className="text-white">Max file size:</strong> 500MB
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#12161c] flex gap-3">
              <button onClick={closeUploadModal} className="btn-secondary btn-md flex-1">
                Cancel
              </button>
              <button
                onClick={() => uploadFile && uploadMutation.mutate(uploadFile)}
                disabled={!uploadFile || uploadMutation.isPending}
                className="btn-primary btn-md flex-1"
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Upload Dataset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDataset && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content w-full max-w-4xl p-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-xl font-bold text-white">Preview: {selectedDataset.name}</h2>
              <button onClick={() => setShowPreviewModal(false)} className="btn-ghost p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="bg-[#181a20] rounded-xl p-4 font-mono text-sm text-[#848e9c] max-h-96 overflow-auto">
                <pre>{`{"text": "Sample data row 1", "label": "positive"}
{"text": "Sample data row 2", "label": "negative"}
{"text": "Sample data row 3", "label": "neutral"}
...`}</pre>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#12161c]">
              <button onClick={() => setShowPreviewModal(false)} className="btn-secondary btn-md">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
